import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import api from "../../api/api";

const RATING_KEYS = [
  "registrationRating",
  "venueRating",
  "foodRating",
  "scheduleRating",
  "culturalProgramRating",
  "hospitalityRating",
  "overallOrganizationRating",
];

function pickValue(record, keys, fallback = "") {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
}

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatAverage(value) {
  if (!Number.isFinite(value)) {
    return "0.0";
  }

  return value.toFixed(1);
}

function normalizeFeedbackItem(item) {
  const firstName = pickValue(item, ["FirstName", "firstName"]);
  const lastName = pickValue(item, ["LastName", "lastName"]);
  const fullName =
    `${firstName} ${lastName}`.trim() ||
    pickValue(item, ["Name", "name"], "Unknown member");
  const year = String(
    pickValue(item, ["YearOfPassedOut", "yearOfPassedOut"], "-"),
  );

  return {
    id: pickValue(item, ["FeedbackId", "feedbackId", "Id", "id"]),
    alumniId: pickValue(item, ["AlumniId", "alumniId"]),
    fullName,
    email: pickValue(item, ["AlumniEmail", "email", "Email"], "-"),
    mobileNumber: pickValue(
      item,
      ["AlumniMobileNumber", "mobileNumber", "MobileNumber"],
      "-",
    ),
    department: pickValue(item, ["Department", "department"], "-"),
    year,
    registrationRating: toNumber(
      pickValue(item, ["RegistrationRating", "registrationRating"]),
    ),
    venueRating: toNumber(pickValue(item, ["VenueRating", "venueRating"])),
    foodRating: toNumber(pickValue(item, ["FoodRating", "foodRating"])),
    scheduleRating: toNumber(
      pickValue(item, ["ScheduleRating", "scheduleRating"]),
    ),
    culturalProgramRating: toNumber(
      pickValue(item, ["CulturalProgramRating", "culturalProgramRating"]),
    ),
    hospitalityRating: toNumber(
      pickValue(item, ["HospitalityRating", "hospitalityRating"]),
    ),
    overallOrganizationRating: toNumber(
      pickValue(item, [
        "OverallOrganizationRating",
        "overallOrganizationRating",
      ]),
    ),
    comments: pickValue(item, ["Comments", "comments"], "-"),
    createdAt: pickValue(item, ["CreatedAt", "createdAt"], ""),
  };
}

function SummaryCard({ label, value, tone = "neutral" }) {
  const backgrounds = {
    neutral: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
    primary: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
    success: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
    warning: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        background: backgrounds[tone] || backgrounds.neutral,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={800} color="#0f172a">
        {value}
      </Typography>
    </Paper>
  );
}

function FeedbackViewer() {
  const currentUser = useSelector((state) => state.user.user) || {};
  const token =
    currentUser.token || currentUser.accessToken || currentUser.jwtToken || "";
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("all");

  useEffect(() => {
    let active = true;

    async function loadFeedback() {
      try {
        setLoading(true);
        setError("");
        const response = await api.getFeedback(token);
        const payload = response?.data || response || {};
        const feedbackList = Array.isArray(payload?.feedback)
          ? payload.feedback
          : Array.isArray(payload?.data?.feedback)
            ? payload.data.feedback
            : Array.isArray(payload)
              ? payload
              : [];

        if (active) {
          setFeedbackItems(feedbackList.map(normalizeFeedbackItem));
        }
      } catch (loadError) {
        if (active) {
          setError(
            loadError?.response?.data?.message ||
              loadError?.message ||
              "Unable to load feedback.",
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadFeedback();

    return () => {
      active = false;
    };
  }, [token]);

  const availableYears = useMemo(() => {
    const years = new Set();

    feedbackItems.forEach((item) => {
      if (item.year && item.year !== "-") {
        years.add(item.year);
      }
    });

    return Array.from(years).sort((left, right) => Number(right) - Number(left));
  }, [feedbackItems]);

  const filteredFeedback = useMemo(() => {
    const query = search.trim().toLowerCase();

    return feedbackItems.filter((item) => {
      const matchesYear = yearFilter === "all" || item.year === yearFilter;
      const matchesSearch =
        !query ||
        [
          item.fullName,
          item.email,
          item.mobileNumber,
          item.department,
          item.comments,
          item.year,
        ].some((value) => value.toString().toLowerCase().includes(query));

      return matchesYear && matchesSearch;
    });
  }, [feedbackItems, search, yearFilter]);

  const summary = useMemo(() => {
    const totals = {
      totalFeedback: filteredFeedback.length,
      uniqueYears: new Set(filteredFeedback.map((item) => item.year)).size,
      averageOverall: 0,
      averageAllCategories: 0,
    };

    if (filteredFeedback.length === 0) {
      return totals;
    }

    const overallSum = filteredFeedback.reduce(
      (sum, item) => sum + item.overallOrganizationRating,
      0,
    );

    const categorySum = filteredFeedback.reduce((sum, item) => {
      return (
        sum +
        RATING_KEYS.reduce((ratingSum, key) => ratingSum + item[key], 0)
      );
    }, 0);

    totals.averageOverall = overallSum / filteredFeedback.length;
    totals.averageAllCategories =
      categorySum / (filteredFeedback.length * RATING_KEYS.length);

    return totals;
  }, [filteredFeedback]);

  return (
    <Box sx={{ bgcolor: "#f8fbff", minHeight: "100vh", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Box>
            <Chip
              label="Admin Feedback Viewer"
              sx={{
                mb: 1.5,
                bgcolor: "rgba(29, 78, 216, 0.1)",
                color: "#1d4ed8",
                fontWeight: 700,
              }}
            />
            <Typography variant="h4" fontWeight={800} color="#0f172a">
              Alumni feedback summary and details
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Review who submitted feedback, their passed out year, and every
              rating category in one place.
            </Typography>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard
                label="Total feedback"
                value={summary.totalFeedback}
                tone="primary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard
                label="Passed out years"
                value={summary.uniqueYears}
                tone="neutral"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard
                label="Average overall rating"
                value={formatAverage(summary.averageOverall)}
                tone="success"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard
                label="Average across all categories"
                value={formatAverage(summary.averageAllCategories)}
                tone="warning"
              />
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: 4,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
            }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                justifyContent="space-between"
              >
                <TextField
                  label="Search feedback"
                  placeholder="Search by name, year, email, department, or comments"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  fullWidth
                />
                <TextField
                  select
                  label="Passed out year"
                  value={yearFilter}
                  onChange={(event) => setYearFilter(event.target.value)}
                  sx={{ minWidth: { xs: "100%", md: 220 } }}
                >
                  <MenuItem value="all">All years</MenuItem>
                  {availableYears.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : null}

              {!loading && filteredFeedback.length === 0 ? (
                <Alert severity="info">
                  No feedback records match the current filters.
                </Alert>
              ) : null}

              {!loading && filteredFeedback.length > 0 ? (
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table sx={{ minWidth: 1700 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Member</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Year</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Department
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Mobile</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Registration
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Venue</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Food</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Schedule</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Cultural Program
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Hospitality
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Overall
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Comments</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Submitted At
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredFeedback.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Stack spacing={0.3}>
                              <Typography fontWeight={700} color="#0f172a">
                                {item.fullName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Alumni ID: {item.alumniId || "-"}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{item.year}</TableCell>
                          <TableCell>{item.department}</TableCell>
                          <TableCell>{item.email}</TableCell>
                          <TableCell>{item.mobileNumber}</TableCell>
                          <TableCell>{item.registrationRating}</TableCell>
                          <TableCell>{item.venueRating}</TableCell>
                          <TableCell>{item.foodRating}</TableCell>
                          <TableCell>{item.scheduleRating}</TableCell>
                          <TableCell>{item.culturalProgramRating}</TableCell>
                          <TableCell>{item.hospitalityRating}</TableCell>
                          <TableCell>
                            <Chip
                              label={`${item.overallOrganizationRating}/5`}
                              color={
                                item.overallOrganizationRating >= 4
                                  ? "success"
                                  : item.overallOrganizationRating >= 3
                                    ? "warning"
                                    : "error"
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 260 }}>
                            {item.comments || "-"}
                          </TableCell>
                          <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}

export default FeedbackViewer;
